import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  faSignInAlt, faSpinner, faEnvelope, faPhone, faKey,
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { createUserEmail, company } from 'src/config';
import { getSendForgotPasswordEmail, getLogin } from 'src/store/auth/auth.selectors';

import styled, { css } from 'styled-components/macro';
import { Form } from '@rocketseat/unform';
import { authActions } from 'src/store/auth/auth.actions';
import { Block } from 'src/components/LayoutBase';
import { LabeledInput } from 'src/components/LabeledInput';
import { withRouter } from 'react-router-dom';
import {
  getBrandingImage,
  imageTypes,
  getBrandingText,
  getBrandingContactInformation,
  contactInformationTypes,
  getBrandingColor,
  colorTypes,
  getBrandingStyleOverride,
} from 'src/branding';
import Menu from 'src/Menu';
import { routes } from '../routes.manifest';
import Button from '../../components/Button';
import {
  Page,
  Column,
  Label,
  Icon,
  Row,
  LinkLabel,
  Cell,
} from '../../components/Layout';
import { ConfirmWrapper } from '../Profile/Profile';

export const LoginPage = styled(Page)`
  color: ${getBrandingColor(colorTypes.fontNormal)};
  background-image:${getBrandingImage('loginPageBg') ? `url(${getBrandingImage('loginPageBg')})` : 'none'};
  ${getBrandingStyleOverride('loginPage')};
  background-size: cover;

  @media screen and (max-width: 520px) {
    ${getBrandingStyleOverride('loginPageMobile')};
    .desktop{
      display: none;
    }
    .mobile{
      display: block;
    }
    &.confirm-profile-info{
      ${getBrandingStyleOverride('confirmProfileInfoPageMobile')};
    }
  }
  .mobile{
    display: none;
  }

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  margin-top: 0;
`;

const loginMessageHidden = {
  maxHeight: 0,
  overflow: 'hidden',
};

const loginMessageExpanded = {
  maxHeight: '36px',
  transition: { duration: 0.3 },
};

const loginMessageExpandedBig = {
  maxHeight: '120px',
  transition: { duration: 0.3 },
};

const loggingInMessageVariants = {
  hidden: loginMessageHidden,
  expanded: loginMessageExpanded,
};

const loggingInBigMessageVariants = {
  hidden: loginMessageHidden,
  expanded: loginMessageExpandedBig,
};

const missingUserVariants = {
  hidden: { maxHeight: 0, overflow: 'hidden' },
  expanded: { maxHeight: '316px', transition: { duration: 0.3 } },
};

const userInOldSystem = {
  hidden: { maxHeight: 0, overflow: 'hidden' },
  expanded: { maxHeight: '150px', transition: { duration: 0.3 } },
};

const LoginStatusMsg = styled.p`
  margin-top: 3px;
  margin-bottom: 3px;
`;

export const FormContentWrapper = styled(Column)`
  padding: 30px;
  border-radius: 1.6em;
  border: 1px solid #e5e3df;
  margin: auto;
  background: ${getBrandingColor(colorTypes.loginFormBg)};
  width: 90%;
  max-width: 370px;
  margin-bottom: 10em;
  width: fit-content;
`;

export const ContentWrapper = styled(Block)`
  margin-top: auto;
  margin-bottom: auto;
`;

const LoginLabel = styled(Label)`
  span {
    text-align: left;
    display: block;
  }
`;

const HeaderWrapper = styled.div`
  text-align: center;
  display: flex;
  margin: auto;
  margin-bottom: 20px;
  width: 90%;
  max-width: 370px;
`;

const LoginLoadingSpinner = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LogoImg = styled.img`
  display: block;
  margin: auto;
  margin-top: 2em;
  align-self: center;
  max-width: 262px;
  height: auto;
  width: 60%;
  z-index: 2;

`;

const LoginFields = styled(Column)`
  ${({ isFetching }) => isFetching
    && css`
      opacity: 0.2;
    `}
`;

const Login = withRouter(({ history, mode = 'login' }) => {
  const dispatch = useDispatch();
  const login = useSelector(getLogin);

  const [
    showRegistrationInstructions,
    setShowRegistrationInstructions,
  ] = useState(false);

  const onSubmitLogin = (form) => {
    setShowRegistrationInstructions(false);
    dispatch(
      authActions.login({ username: form.username, password: form.password }),
    );
  };

  const [invalidUsername, setInvalidUsername] = useState(false);
  const forgotPasswordEmail = useSelector(getSendForgotPasswordEmail, []);

  const onSubmitForgotPassword = (form) => {
    if (!/^[0-9]{1,}$/.test(form.username)) {
      setInvalidUsername(true);
    } else {
      setInvalidUsername(false);
      dispatch(
        authActions.sendForgotPasswordEmail({ username: form.username }),
      );
    }
  };

  const loginFormSchema = Yup.object().shape({
    username: Yup.string().required('Feltet må fylles ut'),
    password: Yup.string().required('Feltet må fylles ut'),
  });

  const usernameWording = company === 'furuseth' ? 'leverandørnummer' : company === 'midtnorge' ? 'brukernavn' : null;

  return (
    <LoginPage title="Logg inn" style={{ padding: '0' }}>
      <Row>
        {company === 'furuseth'
        && <LogoImg src={getBrandingImage(imageTypes.slaktewebLogoBig)} /> }
        {company === 'midtnorge'
        && <Menu hideControls />}
      </Row>
      <ContentWrapper>
        <HeaderWrapper>
          {company === 'furuseth'
          && (
            <LoginLabel
              as="h1"
              size="2.2em"
              color={getBrandingColor(colorTypes.fontHeader)}
            >
              <span>Logg inn</span>
              <span>på din slakteweb</span>
            </LoginLabel>
          ) }

          {company === 'midtnorge'
          && (
            <LoginLabel
              as="h1"
              size="2.4m"
              color="white"
              className="login-page-header"
            >
              <img src={getBrandingImage(imageTypes.slaktewebTxtLogoWhite)} aria-hidden="true" alt="" className="desktop-tablet" style={{ width: '13em' }} />
              <img src={getBrandingImage(imageTypes.slaktewebTxtLogoBlue)} className="mobile" aria-hidden="true" alt="" style={{ width: '13em', maxWidth: '100%' }} />
              <span style={{ textAlign: 'center' }}>LOGG INN PÅ DIN SIDE</span>
            </LoginLabel>
          )}
        </HeaderWrapper>
        {mode === 'login' && (
          <Form onSubmit={onSubmitLogin} schema={loginFormSchema}>
            <FormContentWrapper>
              <Column>
                <LoginFields isFetching={login.isFetching}>
                  <LabeledInput label={company === 'furuseth' ? 'Leverandørnummer' : company === 'midtnorge' ? 'Brukernavn (leverandørnr):' : null} name="username" />

                  <div style={{ position: 'relative' }}>
                    <LabeledInput
                      name="password"
                      label="Passord"
                      type="password"
                    />
                    {company === 'midtnorge'
                    && (
                      <Icon
                        icon={faKey}
                        style={{
                          position: 'absolute',
                          display: 'inline-block',
                          transform: 'translateX(-151%)',
                          top: '2.85em',
                          right: 0,
                          color: getBrandingColor('mnBlue'),
                        }}
                      />
                    ) }

                  </div>
                </LoginFields>
                {login.isFetching && (
                  <LoginLoadingSpinner>
                    <Icon icon={faSpinner} />
                  </LoginLoadingSpinner>
                )}
              </Column>

              <motion.div
                variants={loggingInMessageVariants}
                animate={login.isFetching ? 'expanded' : 'hidden'}
                initial="hidden"
              >
                <LoginStatusMsg>Logger inn ...</LoginStatusMsg>
              </motion.div>
              <motion.div
                key="uneitorized"
                variants={loggingInMessageVariants}
                animate={
                  login.errorcodes
                  && login.errorcodes.indexOf('UNAUTHORIZED') >= 0
                    ? 'expanded'
                    : 'hidden'
                }
                initial="hidden"
              >
                <LoginStatusMsg>Feil brukernavn/passord</LoginStatusMsg>
              </motion.div>
              <motion.div
                key="loginInternalError"
                variants={loggingInMessageVariants}
                animate={
                  login.error && login.errorcodes === undefined
                    ? 'expanded'
                    : 'hidden'
                }
                initial="hidden"
              >
                <LoginStatusMsg>
                  Det oppsto en feil ved innloggingen
                </LoginStatusMsg>
              </motion.div>
              <motion.div
                variants={missingUserVariants}
                key="not_fond"
                animate={
                  login.errorcodes
                  && login.errorcodes.indexOf(
                    'USER_IN_OLD_NO_EMAIL_NOT_NEW_SYSTEM',
                  ) >= 0
                    ? 'expanded'
                    : 'hidden'
                }
                initial="hidden"
              >
                <h3>
                  {getBrandingText('companyName')}
                  {' '}
                  trenger din e-postadresse for
                  overføring til nye slakteweb
                </h3>
                <p>
                  Du har en bruker i gamle slakteweb uten tilknyttet
                  e-epostadresse. For å overføre brukeren til nye slakteweb må
                  du sende en e-post til
                  {' '}
                  {getBrandingText('companyName')}
                  {' '}
                  med
                  ditt fulle navn, leverandørnummer, mobilnummer og
                  e-postadresse.
                  <br />
                  <br />
                  Etter at epostadressen er registrert kan du trykke på
                  &laquo;Glemt passord?&raquo; for å sette et passord for den
                  nye brukeren. Brukeren vil deretter være klar til bruk.
                  <br />
                  <br />
                  Trykk
                  {' '}
                  <a
                    href={`mailto:${getBrandingContactInformation(
                      contactInformationTypes.registerUserWithoutEmailadressEmail,
                    )}?subject=Slakteweb -- Registrering av e-post for bruker`}
                  >
                    her
                  </a>
                  {' '}
                  for å sende en e-post til
                  {' '}
                  {getBrandingText('companyName')}
                  .
                </p>
              </motion.div>

              <motion.div
                variants={userInOldSystem}
                key="not_fond"
                animate={
                  login.errorcodes
                  && login.errorcodes.indexOf('USER_IN_OLD_NOT_NEW_SYSTEM') >= 0
                    ? 'expanded'
                    : 'hidden'
                }
                initial="hidden"
              >
                <h3>E-post sendt</h3>
                <LoginStatusMsg>
                  Det er sendt en e-post med instruksjoner for registrering på
                  nye Slakteweb til din tilknyttede e-postkonto.
                </LoginStatusMsg>
              </motion.div>

              <Cell marginTop="0.6em">
                <Button
                  type="submit"
                  aria-label="Logg inn"
                  primary
                  disabled={login.isFetching}
                >
                  <Row grow>
                    <Label inline size="1.0rem" fontWeight="600" align="center">
                      Logg inn
                    </Label>
                    <Label inline size="1.6rem" marginLeft="auto">
                      <Icon icon={faSignInAlt} />
                    </Label>
                  </Row>
                </Button>
              </Cell>

              <LinkLabel
                to={routes.forgotPw}
                marginLeft="auto"
                style={{
                  color: getBrandingColor(colorTypes.fontHeader),
                  fontSize: '0.9em',
                  marginTop: '1rem',
                  textAlign: 'center',
                }}
                marginRight="auto"
                marginTop="1em"
                align="center"
              >
                Glemt passord?
              </LinkLabel>

              <LinkLabel
                style={{
                  color: getBrandingColor(colorTypes.fontHeader),
                  fontSize: '0.9em',
                  marginTop: '0.1em',
                  textAlign: 'center',
                  textDecoration: 'underline',
                }}
                onClick={() => setShowRegistrationInstructions((old) => !old)}
              >
                Mangler bruker?
              </LinkLabel>

              <motion.div
                variants={missingUserVariants}
                key="not_fond"
                animate={
                  login.errorcodes
                  && login.errorcodes.indexOf('USER_NOT_FOUND') >= 0
                    ? 'expanded'
                    : 'hidden'
                }
                initial="hidden"
              >
                <h3>Bruker ikke funnet</h3>
                <h4>Registreringsinstruksjoner</h4>
                <p>
                  Send en e-post til
                  {' '}
                  {getBrandingText('companyName')}
                  {' '}
                  med ditt
                  fulle navn, mobilnummer og e-postadresse for å få opprettet en
                  bruker.
                  <br />
                  <br />
                  Etter at brukeren er opprettet, kan du trykke på &laquo;Glemt
                  passord?&raquo; for å sette et passord for den nye brukeren.
                  Brukeren vil deretter være klar til bruk.
                  <br />
                  <br />
                  Trykk
                  {' '}
                  <a
                    href={`mailto:${getBrandingContactInformation(
                      contactInformationTypes.registerUserWithoutEmailadressEmail,
                    )}?subject=Slakteweb -- Forespørsel om å opprette brukerkonto`}
                  >
                    her
                  </a>
                  {' '}
                  for å sende en e-post til
                  {' '}
                  {getBrandingText('companyName')}
                  .
                </p>
              </motion.div>

              <motion.div
                variants={missingUserVariants}
                key="not_fond"
                animate={showRegistrationInstructions ? 'expanded' : 'hidden'}
                initial="hidden"
              >
                <h3>Registreringsinstruksjoner</h3>
                <p>
                  Send en e-post til
                  {' '}
                  {getBrandingText('companyName')}
                  {' '}
                  med ditt
                  fulle navn, mobilnummer og e-postadresse for å få opprettet en
                  bruker.
                  <br />
                  <br />
                  Etter at brukeren er opprettet kan du trykke på &laquo;Glemt
                  passord?&raquo; for å sette et passord. Brukeren vil deretter
                  være klar til bruk.
                  <br />
                  <br />
                  Trykk
                  {' '}
                  <a
                    href={`mailto:${createUserEmail}?subject=Slakteweb -- Forespørsel om å opprette brukerkonto`}
                  >
                    her
                  </a>
                  {' '}
                  for å sende en e-post til
                  {' '}
                  {getBrandingText('companyName')}
                  .
                </p>
              </motion.div>

              {company === 'furuseth'
              && (
                <div style={{ marginTop: '1em', textAlign: 'center' }}>
                  <p style={{ marginBottom: '0.8em' }}>
                    {' '}
                    Trenger du hjelp? Ta kontakt
                  </p>

                  <p
                    style={{
                      marginBottom: '0.5em',
                      marginTop: '0',
                    }}
                  >
                    Epost:
                    {' '}
                    <a
                      href={`mailto:${getBrandingContactInformation(
                        contactInformationTypes.loginSupportEmail,
                      )}`}
                      style={{
                        color: getBrandingColor(colorTypes.linkSecondary),
                      }}
                    >
                      {getBrandingContactInformation(
                        contactInformationTypes.loginSupportEmail,
                      )}
                    </a>
                  </p>
                  <p style={{ marginTop: '0' }}>
                    Telefon:
                    {' '}
                    {getBrandingContactInformation(
                      contactInformationTypes.loginSupportPhone,
                    )}
                  </p>
                </div>
              ) }

              {company === 'midtnorge'
              && (
                <div style={{ marginTop: '1em', textAlign: 'center' }}>
                  <p style={{ marginBottom: '0.8em', fontWeight: '600' }}>
                    {' '}
                    Trenger du hjelp? Ta kontakt.
                  </p>

                  <p
                    style={{
                      marginBottom: '0.5em',
                      marginTop: '0',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon={faEnvelope} style={{ color: getBrandingColor('mnBlue'), alignSelf: 'center', marginRight: '0.4em' }} />
                    <a
                      href={`mailto:${getBrandingContactInformation(
                        contactInformationTypes.loginSupportEmail,
                      )}`}
                      style={{
                        color: getBrandingColor(colorTypes.linkSecondary),
                      }}
                    >
                      {getBrandingContactInformation(
                        contactInformationTypes.loginSupportEmail,
                      )}
                    </a>
                  </p>
                  <p style={{
                    marginTop: '0',
                    display: 'flex',
                    justifyContent: 'center',

                  }}
                  >
                    <Icon icon={faPhone} style={{ color: getBrandingColor('mnBlue'), alignSelf: 'center', marginRight: '0.4em' }} />
                    {getBrandingContactInformation(
                      contactInformationTypes.loginSupportPhone,
                    )}
                  </p>
                </div>
              ) }
            </FormContentWrapper>
          </Form>
        )}

        {mode === 'forgotPassword' && (
          <Form onSubmit={onSubmitForgotPassword}>
            <FormContentWrapper>
              <Label as="h1" size="1.3rem" marginBottom="2rem">
                Glemt passord
              </Label>
              <Label color={getBrandingColor(colorTypes.fontSecondary)}>
                Skriv inn ditt
                {' '}
                {usernameWording}
                {' '}
                og få lenke for å sette nytt
                passord på din tilknyttede e-post.
              </Label>
              <Column>
                <Row marginTop="40px">
                  <LabeledInput
                    label={company === 'furuseth' ? 'Leverandørnummer' : company === 'midtnorge' ? 'Brukernavn (leverandørnr)' : null}
                    name="username"
                  />
                </Row>

                <motion.div
                  key="couldNotSendEmail"
                  variants={loggingInMessageVariants}
                  animate={invalidUsername ? 'expanded' : 'hidden'}
                >
                  <Row>
                    <p>
                      Ugyldig
                      {usernameWording}
                    </p>
                  </Row>
                </motion.div>

                <motion.div
                  key="couldNotSendEmail"
                  variants={loggingInMessageVariants}
                  animate={
                    forgotPasswordEmail.isFetching ? 'expanded' : 'hidden'
                  }
                >
                  <Row>
                    <p>Sender e-post...</p>
                  </Row>
                </motion.div>

                <motion.div
                  key="couldNotSendEmail"
                  variants={loggingInBigMessageVariants}
                  animate={forgotPasswordEmail.success ? 'expanded' : 'hidden'}
                >
                  <Row>
                    <p>
                      Dersom en konto med oppgitt
                      {' '}
                      {usernameWording}
                      {' '}
                      finnes og
                      vedkommende har oppgitt en e-postadresse vil en e-post med
                      en lenke for passordendring være sendt.
                    </p>
                  </Row>
                </motion.div>

                <motion.div
                  key="couldNotSendEmail"
                  variants={loggingInMessageVariants}
                  animate={forgotPasswordEmail.error ? 'expanded' : 'hidden'}
                >
                  <Row>
                    <p>Kunne ikke sende e-post.</p>
                  </Row>
                </motion.div>
                <Row justify="center">
                  <Button
                    secondary
                    type="button"
                    margin="0 10px 0 0"
                    onClick={() => {
                      history.push('/login');
                    }}
                  >
                    Avbryt
                  </Button>
                  <Button type="submit" primary>
                    Send e-post
                  </Button>
                </Row>
              </Column>
            </FormContentWrapper>
          </Form>
        )}
      </ContentWrapper>
    </LoginPage>
  );
});

export default Login;
